/**
 * The URL contract for programmatic pages (stations, lines).
 *
 * Three things have to agree about where a station page lives, and
 * until TASK-592 they did not:
 *
 * 1. `generate-sitemap.mjs` advertised `/<locale>/stations/<slug>`
 * 2. `prerender-stations.mjs` wrote `dist/stations/<slug>/index.html`
 *    and set `<link rel=canonical>` to the locale-less URL
 * 3. nginx 302-redirects `/stations/…` to `/<locale>/stations/…` and
 *    then falls back to `/index.html` for anything it cannot find
 *
 * The result was silent and total: every one of the 272 station URLs in
 * the sitemap returned HTTP 200 serving the SPA shell — home's `<title>`,
 * home's description, `<link rel="canonical" href="https://london-tube.com/">`
 * and no JSON-LD. Google was told 272 times that a station page is a
 * duplicate of the home page. Measured live on 2026-07-22; the same held
 * for every line page.
 *
 * Rewriting titles (TASK-588) or adding structured data (TASK-591) is
 * worth nothing while that is true, so the fix is a single source for
 * the scheme: the canonical URL and the `dist/` path are computed from
 * one function each, and `siteUrls.test.ts` asserts they cannot drift.
 *
 * Locale scope: station, line and journey pages are prerendered for the
 * 16 locales in {@link SiteUrls.PROGRAMMATIC_LOCALES} — their template
 * copy is translated via `public/locales/<locale>/prerender.json`
 * (plan-A1, 2026-09), so none of these URLs is English text under a
 * foreign prefix. Route pages localize the same way (plan-A3):
 * `routeJourney.mjs` composes its copy through the same catalog.
 *
 * 16, not the 87 the app speaks: thirteen locales carried a
 * byte-identical copy of a base locale's catalog and so published a
 * byte-identical page under their own prefix (TASK-875), and 58 more
 * earned under 1% of clicks between them and were retired on
 * 2026-10-08. See `scripts/programmaticLocales.mjs` for both lists and
 * why the survivor of each duplicate group is the one it is.
 */
import { DEFAULT_LOCALE, BASE_URL, LOCALES } from './locales.mjs'

/**
 * Regional English prefixes with no copy of their own on the home and
 * legal pages (TASK-884). `en` already is British English (`og:locale`
 * `en_GB`); rendered, these pages are byte-identical to `en` apart from
 * the self-references, so each one was a self-canonical duplicate —
 * Search Console listed `/en-GB`, `/en-AU`, `/en-CA`, `/en-IN` under
 * «Duplicate, Google chose different canonical» on 2026-10-26. nginx
 * 301s them onto `en`; `$best_locale` never negotiates to them.
 */
const REGIONAL_ENGLISH = Object.freeze(['en-AU', 'en-CA', 'en-GB', 'en-IN', 'en-US'])
import { PROGRAMMATIC_LOCALES } from './programmaticLocales.mjs'

export const SiteUrls = Object.freeze({
  /**
   * Locales that station / line / journey pages are actually prerendered
   * for. Owned by `scripts/programmaticLocales.mjs`, which is also where
   * the thirteen dropped prefixes and their redirect targets live.
   */
  PROGRAMMATIC_LOCALES,

  /** See {@link REGIONAL_ENGLISH}. */
  REGIONAL_ENGLISH,

  /**
   * Locales the home page is prerendered, listed in the sitemap and
   * clustered in hreflang for: every supported locale but the regional
   * English ones, which 301 onto `en` (TASK-884).
   */
  HOME_LOCALES: Object.freeze(LOCALES.filter((locale) => !REGIONAL_ENGLISH.includes(locale))),

  /**
   * Locales the legal pages (privacy, terms, cookies) are published in:
   * English only. The documents are written in English and only in
   * English — under any other prefix the prerender carried a translated
   * `<h1>` and nothing else, and the SPA then rendered the English text,
   * so each of the 81 other prefixes was a thin copy of `/en/<page>`.
   * Search Console listed `/ms/privacy-policy`, `/jv/cookie-policy`,
   * `/es-MX/privacy-policy`, `/fr-FR/cookie-policy` … under «Duplicate,
   * Google chose different canonical» (2026-10-27). nginx 301s every
   * other prefix onto `/en/<page>`.
   */
  LEGAL_LOCALES: Object.freeze([DEFAULT_LOCALE]),

  /**
   * Locales the station DETAIL surface (`/<locale>/station/<naptanId>`)
   * is prerendered for — every one of them has BOTH a translated
   * `station-page` catalog and a translated description overlay for
   * every station (asserted by `stationDetailUrls.test.ts`), so none of
   * these URLs is English text under a foreign prefix. The remaining
   * ~72 supported locales stay SPA-only and out of sitemap/hreflang.
   *
   * Selected from ASC/app geo (tourist origin markets) rather than from
   * Search Console: GSC cannot show demand for pages that do not exist
   * yet (l10n spec, "SEO alignment"). Widen it as GSC reports per-locale
   * impressions after launch.
   */
  DETAIL_LOCALES: Object.freeze([
    DEFAULT_LOCALE,
    'de',
    'fr',
    'es',
    'es-419',
    'it',
    'pt-BR',
    'ja',
    'ko',
    'zh-Hant',
    'zh-Hans',
    'nl',
    'pl',
    'tr',
    'ru',
  ]),

  /** Locale-less route for a station page, e.g. `/stations/barons-court`. */
  stationPath(slug) {
    return `/stations/${slug}`
  },

  /**
   * Locale-less route for the station DETAIL page, e.g.
   * `/station/940GZZLUBST`. Keyed by naptanId — the same key the
   * content records and the SPA route use, so a station rename never
   * orphans the URL or its data. Distinct on purpose from
   * {@link SiteUrls.stationPath}, which is the interactive MAP surface
   * (decision B, 2026-08-05: two indexed surfaces, split by intent).
   */
  stationDetailPath(naptanId) {
    return `/station/${naptanId}`
  },

  /** Locale-less route for a line page, e.g. `/lines/central`. */
  linePath(slug) {
    return `/lines/${slug}`
  },

  /**
   * Locale-less route for a journey page, e.g.
   * `/routes/paddington-to-baker-street`.
   *
   * Deliberately `/routes/` and not `/route/`: `/route` is the SPA's
   * interactive planner, whose from/to come from user input and which
   * `robots.txt` keeps out of the index. These are a fixed, curated set
   * of prerendered pages and are the opposite — the only route URLs that
   * should be indexed.
   */
  routePath(fromSlug, toSlug) {
    return `/routes/${fromSlug}-to-${toSlug}`
  },

  /**
   * Absolute canonical URL. No trailing slash — nginx 301s
   * `/<locale>/<path>/` to the slash-less form, so a canonical with one
   * would point at a redirect.
   */
  canonical(path, locale = DEFAULT_LOCALE) {
    return `${BASE_URL}/${locale}${path}`
  },

  /**
   * Site-relative form of {@link SiteUrls.canonical}, for `href`s inside
   * the site. Internal links must carry the locale prefix — the
   * locale-less form is a 302, and a link to a redirect spends crawl
   * budget arriving at the page it could have linked to directly.
   */
  href(path, locale = DEFAULT_LOCALE) {
    return `/${locale}${path}`
  },

  /**
   * Path segments under `dist/` that nginx `try_files` resolves for
   * {@link SiteUrls.canonical} of the same arguments. The emitted file is
   * `<segments>/index.html`.
   */
  distSegments(path, locale = DEFAULT_LOCALE) {
    return [locale, ...path.split('/').filter(Boolean)]
  },
})
